import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { User } from "react-feather";
import { FORMS, MODULES } from "../../const";
import { policeDivisionValidation } from "../validations/PoliceDivisionCreation";
import LoadingSpinner from "../LoadingSpinner";
import Input from "../input/Input";
import Dropdown from "../input/Dropdown";
import InfoList from "../InfoList";
import { formatFromJsonDate } from "../../dateConfig";
import useUserCompany from "../../hooks/useUserCompany";
import SearchDropdown from "../input/SearchDropdown";
import { countryStartsWith } from "../../api/countryApi";
import { stateByCountryAndStartsWith } from "../../api/stateApi";

const FORM = FORMS.POLICE_DIVISION.POL.form;

const PoliceDivisionAddComponent = forwardRef(
  ({ errors, dropdown, isLoading, currentValues, editMode }, ref) => {
    const userCompany = useUserCompany();
    const [company, setCompany] = useState(
      userCompany ? userCompany.comId : null
    );

    useEffect(() => {
      setCompany(userCompany ? userCompany.comId : null);
    }, [userCompany]);

    const [policeDivisionData, setPoliceDivisionData] = useState(
      editMode
        ? {
            ...currentValues,
            rpoCode: currentValues?.rpoCode || "",
            rpoComId: currentValues?.rpoComId || company,
          }
        : {
            rpoCode: "",
            rpoName: "",
            rpoPoliceStation: "",
            rpoCountry: "",
            rpoState: "",
            rpoDescription: "",
            rpoStatus: 2,
            rpoActive: 1,
            rpoComId: company,
          }
    );

    const [currentErrors, setCurrentErrors] = useState({});
    const [resetState, setResetState] = useState(false);
    const [countryResetState, setCountryResetState] = useState(false);

    useEffect(() => {
      if (errors) {
        setCurrentErrors(errors.policeDivisionErrors || errors || {});
      } else {
        setCurrentErrors({});
      }
    }, [errors]);

    useEffect(() => {
      if (editMode && currentValues) {
        setCurrentErrors({});
        setPoliceDivisionData({
          ...currentValues,
          rpoCode: currentValues.rpoCode || "",
          rpoComId: currentValues.rpoComId || company || userCompany?.comId || 1,
          rpoCountry: currentValues.rpoCountry || "",
          rpoState: currentValues.rpoState || "",
        });
        setResetState(true);
        setCountryResetState(true);
      } else if (!editMode) {
        setCurrentErrors({});
        setPoliceDivisionData({
          rpoCode: "",
          rpoName: "",
          rpoPoliceStation: "",
          rpoCountry: "",
          rpoState: "",
          rpoDescription: "",
          rpoStatus: 2,
          rpoActive: 1,
          rpoComId: company || userCompany?.comId || 1,
        });
        setResetState(true);
        setCountryResetState(true);
      }
    }, [editMode, currentValues, company, userCompany]);

    useImperativeHandle(ref, () => ({
      getData: () => {
        const companyId = company || userCompany?.comId || policeDivisionData.rpoComId || 1;
        const payload = {
          rpoCompanyId: Number(companyId) || 1,
          rpoComId: Number(companyId) || 1,
          rpoName: policeDivisionData.rpoName ? policeDivisionData.rpoName.trim() : "",
          rpoPoliceStation: policeDivisionData.rpoPoliceStation ? policeDivisionData.rpoPoliceStation.trim() : null,
          rpoDescription: policeDivisionData.rpoDescription ? policeDivisionData.rpoDescription.trim() : null,
          rpoCountry: policeDivisionData.rpoCountry
            ? Number(policeDivisionData.rpoCountry) || policeDivisionData.rpoCountry
            : null,
          rpoState: policeDivisionData.rpoState
            ? Number(policeDivisionData.rpoState) || policeDivisionData.rpoState
            : null,
          rpoStatus: Number(policeDivisionData.rpoStatus) || 2,
          rpoActive: Number(policeDivisionData.rpoActive) || 1,
        };
        if (editMode && policeDivisionData.rpoId) {
          payload.rpoId = policeDivisionData.rpoId;
          payload.rpoCode = policeDivisionData.rpoCode || null;
        }
        return payload;
      },
      validateForm: () => {
        return policeDivisionValidation(policeDivisionData);
      },
      resetForm: () => {
        setCurrentErrors({});
        setPoliceDivisionData({
          rpoCode: "",
          rpoName: "",
          rpoPoliceStation: "",
          rpoCountry: "",
          rpoState: "",
          rpoDescription: "",
          rpoStatus: 2,
          rpoActive: 1,
          rpoComId: company || userCompany?.comId || 1,
        });
        setResetState(true);
        setCountryResetState(true);
      },
    }));

    return (
      <>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <form
            className="ui form"
            style={{ overflowY: "auto", height: "75vh" }}
          >
            <div className="ui segment">
              <h4 className="ui dividing header">Police Division Information</h4>

              <div className="two fields">
                <Input
                  label="Police Division Code"
                  placeholder="Auto-generated"
                  module={MODULES.Master}
                  form={FORM}
                  field="rpo_code"
                  editMode={editMode}
                  disabled={true}
                  name="rpoCode"
                  value={editMode ? policeDivisionData.rpoCode : ""}
                  onChange={() => {}}
                />
                <Input
                  required={true}
                  label="Police Division Name"
                  placeholder="Enter Name"
                  module={MODULES.Master}
                  form={FORM}
                  field="rpo_name"
                  editMode={editMode}
                  error={currentErrors.rpoName}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { rpoName: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="rpoName"
                  value={policeDivisionData.rpoName}
                  onChange={(e) => {
                    setPoliceDivisionData((prevState) => ({
                      ...prevState,
                      rpoName: e.target.value,
                    }));
                  }}
                />
              </div>

              <div className="two fields">
                <SearchDropdown
                  clearable={true}
                  required={true}
                  editMode={editMode}
                  reset={countryResetState}
                  setReset={setCountryResetState}
                  threshold={1}
                  module={MODULES.Master}
                  form={FORM}
                  field="rpo_country"
                  onResultSelect={(data) => {
                    setPoliceDivisionData((prev) => ({
                      ...prev,
                      rpoCountry: data,
                      rpoState: "",
                    }));
                    setResetState(true);
                  }}
                  onClear={() => {
                    setPoliceDivisionData((prev) => ({
                      ...prev,
                      rpoCountry: "",
                      rpoState: "",
                    }));
                    setResetState(true);
                  }}
                  api={countryStartsWith}
                  fieldInfo={{
                    tableName: "masterdata.tms_m_reg_country",
                    primaryKey: "cou_id",
                    fieldName: "cou_country",
                    id: policeDivisionData.rpoCountry,
                  }}
                  label="Country"
                  error={currentErrors.rpoCountry}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { rpoCountry: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                />
                <SearchDropdown
                  clearable={true}
                  editMode={editMode}
                  reset={resetState}
                  setReset={setResetState}
                  module={MODULES.Master}
                  form={FORM}
                  field="rpo_state"
                  onResultSelect={(data) => {
                    setPoliceDivisionData((prev) => ({
                      ...prev,
                      rpoState: data,
                    }));
                  }}
                  onClear={() => {
                    setPoliceDivisionData((prev) => ({
                      ...prev,
                      rpoState: "",
                    }));
                  }}
                  api={stateByCountryAndStartsWith}
                  apiDependency={{ id: policeDivisionData.rpoCountry }}
                  fieldInfo={{
                    tableName: "masterdata.tms_m_reg_state",
                    primaryKey: "stt_id",
                    fieldName: "stt_state_name",
                    id: policeDivisionData.rpoState,
                  }}
                  label="State"
                  error={currentErrors.rpoState}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { rpoState: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  disabled={!policeDivisionData.rpoCountry}
                />
              </div>

              <div className="two fields">
                <Input
                  label="Police Station"
                  placeholder="Enter Police Station"
                  module={MODULES.Master}
                  form={FORM}
                  field="rpo_police_station"
                  editMode={editMode}
                  error={currentErrors.rpoPoliceStation}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { rpoPoliceStation: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="rpoPoliceStation"
                  value={policeDivisionData.rpoPoliceStation}
                  onChange={(e) => {
                    setPoliceDivisionData((prevState) => ({
                      ...prevState,
                      rpoPoliceStation: e.target.value,
                    }));
                  }}
                />
                <Input
                  label="Description"
                  placeholder="Enter Description"
                  module={MODULES.Master}
                  form={FORM}
                  field="rpo_description"
                  editMode={editMode}
                  error={currentErrors.rpoDescription}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { rpoDescription: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="rpoDescription"
                  value={policeDivisionData.rpoDescription}
                  onChange={(e) => {
                    setPoliceDivisionData((prevState) => ({
                      ...prevState,
                      rpoDescription: e.target.value,
                    }));
                  }}
                />
              </div>

              <div className="two fields">
                <Dropdown
                  label="Status"
                  module={MODULES.Master}
                  form={FORM}
                  field="rpo_status"
                  editMode={editMode}
                  error={currentErrors.rpoStatus}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { rpoStatus: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  placeholder="Select Status"
                  options={dropdown.statusOpt}
                  value={policeDivisionData.rpoStatus}
                  style={{ height: "20px" }}
                  onChange={(e, data) => {
                    setPoliceDivisionData((prevState) => ({
                      ...prevState,
                      rpoStatus: data.value,
                    }));
                  }}
                />
                <Dropdown
                  label="Active"
                  field="rpo_active"
                  editMode={editMode}
                  error={currentErrors.rpoActive}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { rpoActive: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  placeholder="Select Active"
                  options={dropdown.activeOpt}
                  value={policeDivisionData.rpoActive}
                  style={{ height: "20px" }}
                  onChange={(e, data) => {
                    setPoliceDivisionData((prevState) => ({
                      ...prevState,
                      rpoActive: data.value,
                    }));
                  }}
                />
              </div>
            </div>

            {editMode && (
              <InfoList
                title="User Information"
                content={[
                  {
                    title: "Created By",
                    value: policeDivisionData.createdBy
                      ? policeDivisionData.createdBy
                      : "N/A",
                  },
                  {
                    title: "Created Date",
                    value: policeDivisionData.createdDate
                      ? formatFromJsonDate(policeDivisionData.createdDate)
                      : "N/A",
                  },
                  {
                    title: "Last Modified By",
                    value: policeDivisionData.lastModifiedBy
                      ? policeDivisionData.lastModifiedBy
                      : "N/A",
                  },
                  {
                    title: "Last Modified Date",
                    value: policeDivisionData.lastModifiedDate
                      ? formatFromJsonDate(policeDivisionData.lastModifiedDate)
                      : "N/A",
                  },
                ]}
                Icon={User}
              />
            )}
          </form>
        )}
      </>
    );
  }
);

export default PoliceDivisionAddComponent;


